"use client"

import { useState, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useParams, Link, useNavigate } from "react-router-dom"
import {
  StarIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  BriefcaseIcon,
  GlobeAltIcon,
  BuildingOffice2Icon,
  UsersIcon,
  ExclamationTriangleIcon,
  CurrencyRupeeIcon,
  ClockIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline"
import {
  Button,
  Box,
  Text,
  Modal,
  ModalBody,
  ModalContent,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Textarea,
  Input,
  VStack,
  useDisclosure,
  Select,
  Skeleton,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useToast,
  GridItem,
  Image,
  Badge,
  FormControl,
} from "@chakra-ui/react"
import { TimeDifference } from "../common/TimeDifference"
import { fetchCompanyDetailsAsync, selectCompanyDetails } from "./CompanySingleSlice/CompanySingleSlice"
import { fetchJobListByCompanyAsync, selectCompanyJobs } from "./JobsByCompanySlice/JobsByCompanySlice"
import { fetchPublicJobTitlesAsync, selectJobTitles } from "../AdminPanel/JobPostingSlice/JobTitles/JobTitlesSlice"
import {
  addCompanyReviewAsync,
  fetchPublicCompanyReviewsAsync,
  selectPublicCompanyReviews,
} from "../Public/PublicCompanyReviews/PublicCompanyReviewsSlice"
import { EMPLOYER_COMPANY_LOGO_SAVE_URL, EMPLOYER_COMPANY_MEDIA_URL } from "../common/URLs"
import companylogo from "./logo1png.png"
import { updateBookmarkAsync } from "../candidateDashboard/candidateSlice/SavedJobs/SavedJobsSlice"
import { Footer } from "../footer/footer"
import { NoDataFound } from "../../pages/CommonPublicPages/Basic/NoDataFound"
import { fetchUserRole } from "../common/FetchUserRole"
import { fetchJobCityListAsync, selectJobCity } from "../AdminPanel/Address/JobCityList/JobCityListSlice"

// Loading Skeleton Components
const CompanyProfileSkeleton = () => (
  <div className="min-h-screen bg-white animate-pulse">
    <div className="h-[90px] bg-gradient-to-r from-blue-700 via-sky-300 to-sky-100 rounded-b-3xl">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <Skeleton height="40px" width="200px" />
      </div>
    </div>
    <div className="max-w-7xl mx-auto px-6 -mt-16">
      <Skeleton height="120px" width="120px" borderRadius="full" />
      <div className="mt-6">
        <Skeleton height="40px" width="300px" mb={4} />
        <Skeleton height="24px" width="200px" mb={4} />
        <Skeleton height="20px" width="400px" />
      </div>
    </div>
    <div className="max-w-7xl mx-auto px-6 py-8 mt-8">
      <div className="grid md:grid-cols-3 gap-8">
        <div className="md:col-span-2">
          <Skeleton height="200px" mb={4} borderRadius="lg" />
          <Skeleton height="150px" mb={4} borderRadius="lg" />
          <Skeleton height="150px" borderRadius="lg" />
        </div>
        <Skeleton height="500px" borderRadius="lg" />
      </div>
    </div>
  </div>
)

const JobCardSkeleton = () => (
  <div className="border border-gray-200 rounded-xl p-4 animate-pulse shadow-sm">
    <div className="flex items-center space-x-4 mb-4">
      <div className="w-16 h-16 bg-gray-200 rounded-full" />
      <div className="flex-1">
        <div className="h-6 bg-gray-200 rounded w-3/4 mb-2" />
        <div className="h-4 bg-gray-200 rounded w-1/2" />
      </div>
    </div>
    <div className="space-y-3">
      <div className="h-4 bg-gray-200 rounded w-full" />
      <div className="h-4 bg-gray-200 rounded w-5/6" />
      <div className="h-4 bg-gray-200 rounded w-4/6" />
    </div>
  </div>
)

// Empty State Component
const EmptyState = ({ message, icon: Icon }) => (
  <div className="flex flex-col items-center justify-center p-8 text-center bg-white rounded-xl shadow-sm border border-gray-100">
    <div className="w-20 h-20 mb-6 text-gray-300">{Icon}</div>
    <Text color="gray.500" fontSize="lg" fontWeight="medium">
      {message}
    </Text>
  </div>
)

// Error State Component
const ErrorState = ({ message }) => (
  <Alert
    status="error"
    variant="subtle"
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    textAlign="center"
    height="200px"
    borderRadius="xl"
    className="my-4 shadow-sm"
  >
    <AlertIcon boxSize="40px" mr={0} />
    <AlertTitle mt={4} mb={1} fontSize="lg">
      Error Loading Data
    </AlertTitle>
    <AlertDescription maxWidth="sm">{message}</AlertDescription>
  </Alert>
)

const CompanyOverview = ({ companyProfile, companyJobs, reviews, handleOpenReviewModal, isLoading, error }) => {
  if (isLoading) return <CompanyProfileSkeleton />
  if (error) return <ErrorState message={error} />
  if (!companyProfile)
    return (
      <EmptyState message="Company profile not found" icon={<ExclamationTriangleIcon className="w-full h-full" />} />
    )
  const totalReviews = reviews?.length || 0;
  const avgRating =
    totalReviews > 0
      ? (reviews.reduce((sum, r) => sum + r.review_rating, 0) / totalReviews).toFixed(1)
      : 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Gradient Header Banner */}
      <div className="h-[90px] bg-gradient-to-r from-blue-700 via-sky-300 to-sky-100

 rounded-b-3xl">

      </div>

      {/* Company Profile Section */}
      <div className="w-full sm:px-24 px-6 -mt-16">
        {/* Company Logo */}
        <div className="w-36 h-36 rounded-full overflow-hidden border-4 border-white shadow-lg bg-white flex items-center justify-center p-2">
          <img
            src={
              companyProfile.company_logo
                ? `${EMPLOYER_COMPANY_LOGO_SAVE_URL}${companyProfile.company_logo}`
                : companylogo
            }
            alt={companyProfile.company_name}
            className="w-full h-full object-contain"
          />
        </div>

        {/* Company Title and Info */}
        <div className="mt-6">
          <h1 className="text-3xl text-left font-bold text-gray-900">{companyProfile.company_name}</h1>
          <p className="text-gray-600 mt-1">
            {companyProfile.city?.name || "City"}, {companyProfile.state?.name || "State"},{" "}
            {companyProfile.country?.name || "Country"} • {companyProfile.industry_type || "Technology"}
          </p>

          <div className="flex items-center gap-2 mt-4">
            <StarIcon className="w-5 h-5 text-yellow-500 fill-yellow-400" />
            <span className="font-semibold text-gray-800">{avgRating || 0}/5</span>
            <span className="text-gray-500">({totalReviews} reviews)</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="w-full sm:px-24 px-6 py-10">
        <div className="grid md:grid-cols-3 gap-10">
          {/* Left Column - Main Content */}
          <div className="md:col-span-2 overflow-hidden">
            <Tabs variant="unstyled">
              <TabList className="flex flex-wrap  space-x-2 md:space-x-6 border-b border-gray-200 mb-8">
                <Tab
                  className="pb-2 font-medium text-gray-600 border-b-2 border-transparent _selected:text-blue-600 _selected:border-blue-600"
                  _selected={{ color: "blue.600", borderColor: "blue.600" }}
                >
                  Overview
                </Tab>
                <Tab
                  className="pb-2 font-medium text-gray-600 border-b-2 border-transparent _selected:text-blue-600 _selected:border-blue-600"
                  _selected={{ color: "blue.600", borderColor: "blue.600" }}
                >
                  Interview Process
                </Tab>
                <Tab
                  className="pb-2 font-medium text-gray-600 border-b-2 border-transparent _selected:text-blue-600 _selected:border-blue-600"
                  _selected={{ color: "blue.600", borderColor: "blue.600" }}
                >
                  Jobs
                </Tab>
                <Tab
                  className="pb-2 font-medium text-gray-600 border-b-2 border-transparent _selected:text-blue-600 _selected:border-blue-600"
                  _selected={{ color: "blue.600", borderColor: "blue.600" }}
                >
                  Media
                </Tab>
              </TabList>
              <TabPanels>
                <TabPanel className="px-0">
                  <CompanyDetails companyProfile={companyProfile} />
                </TabPanel>
                <TabPanel className="px-0">
                  <InterviewProcess companyProfile={companyProfile} />
                </TabPanel>
                <TabPanel className="px-0">
                  <JobsList jobs={companyJobs} isLoading={isLoading} />
                </TabPanel>
                <TabPanel className="px-0">
                  <CompanyMedia companyProfile={companyProfile} />
                </TabPanel>
              </TabPanels>
            </Tabs>
          </div>

          {/* Right Column - Sidebar */}
          <div>
            <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100 mb-8">
              <h3 className="text-lg font-semibold mb-4">Company Information</h3>

              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <MapPinIcon className="w-5 h-5 text-gray-500 mt-0.5" />
                  <div>
                    <p className="text-gray-900 font-medium">Location</p>
                    <p className="text-gray-600 text-sm">
                      {companyProfile.city?.name || "City"}, {companyProfile.country?.name || "Country"}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <BuildingOffice2Icon className="w-5 h-5 text-gray-500 mt-0.5" />
                  <div>
                    <p className="text-gray-900 font-medium">Industry</p>
                    <p className="text-gray-600 text-sm capitalize">{companyProfile.industry_type || "Technology"}</p>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <UsersIcon className="w-5 h-5 text-gray-500 mt-0.5" />
                  <div>
                    <p className="text-gray-900 font-medium">Company Size</p>
                    <p className="text-gray-600 text-sm">{companyProfile.number_of_employee || "1000+"} employees</p>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <EnvelopeIcon className="w-5 h-5 text-gray-500 mt-0.5" />
                  <div>
                    <p className="text-gray-900 font-medium">Email</p>
                    <p className="text-gray-600 text-sm">{companyProfile.email}</p>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <PhoneIcon className="w-5 h-5 text-gray-500 mt-0.5" />
                  <div>
                    <p className="text-gray-900 font-medium">Phone</p>
                    <p className="text-gray-600 text-sm">{companyProfile.phone}</p>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <GlobeAltIcon className="w-5 h-5 text-gray-500 mt-0.5" />
                  <div>
                    <p className="text-gray-900 font-medium">Website</p>
                    <a
                      href={companyProfile.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline text-sm"
                    >
                      {companyProfile.url}
                    </a>
                  </div>
                </div>
              </div>

              <div className="mt-6">
                <Button
                  colorScheme="blue"
                  variant="solid"
                  size="md"
                  width="full"
                  borderRadius="md"
                  onClick={handleOpenReviewModal}
                >
                  Write a Review
                </Button>
              </div>
            </div>

            <ReviewsSidebar reviews={reviews} isLoading={isLoading} />
          </div>
        </div>
      </div>
    </div>
  )
}

const CompanyDetails = ({ companyProfile }) => (
  <div className="space-y-8">
    <div>
      <h2 className="text-2xl font-bold mb-6 text-gray-800">Overview</h2>
      <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
        <Text className="text-gray-700 leading-relaxed">
          {companyProfile.company_description || "Company description not available"}
        </Text>
      </div>
    </div>

    <div>
      <h2 className="text-2xl font-bold mb-6 text-gray-800">Contact Information</h2>
      <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <MapPinIcon className="w-5 h-5 text-gray-500" />
            <span className="text-gray-700">
              {companyProfile.address1}, {companyProfile.city?.name}, {companyProfile.country?.name}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <PhoneIcon className="w-5 h-5 text-gray-500" />
            <span className="text-gray-700">{companyProfile.phone}</span>
          </div>
          <div className="flex items-center gap-3">
            <EnvelopeIcon className="w-5 h-5 text-gray-500" />
            <span className="text-gray-700">{companyProfile.email}</span>
          </div>
          <div className="flex items-center gap-3">
            <GlobeAltIcon className="w-5 h-5 text-gray-500" />
            <a
              href={companyProfile.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              {companyProfile.url}
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
)

const InterviewProcess = ({ companyProfile }) => {
  const hasInterviewProcess = companyProfile?.company_interview_process?.length > 0
  const steps = hasInterviewProcess ? companyProfile.company_interview_process : []

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6 text-gray-800">Interview Process</h2>

      {hasInterviewProcess ? (
        <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
          <div className="space-y-8">
            {steps.map((step, index) => (
              <div key={index} className="flex gap-4">
                <div className="flex flex-col items-center">
                  <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-sm">
                    {index + 1}
                  </div>
                  {index < steps.length - 1 && <div className="w-0.5 h-full bg-blue-200 mt-2" />}
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-800">{step.title}</h3>
                  <p className="text-gray-600 mt-2">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <GridItem colSpan={{ base: 1, md: 2 }}>
          <div>
            <NoDataFound
              title="Interview Process not available."
              message="We couldn't find any Interview Process for this company. Please check back later."
            />
          </div>
        </GridItem>
      )}
    </div>
  )
}

const JobsList = ({ jobs, isLoading }) => {
  const dispatch = useDispatch()
  const toast = useToast()
  const [bookmarkedJobs, setBookmarkedJobs] = useState({})
  const [appliedJobs, setAppliedJobs] = useState({})
  const navigate = useNavigate()

  // Prefill applied jobs state when jobs are loaded
  useEffect(() => {
    if (jobs?.length > 0) {
      const initialAppliedJobs = {}
      jobs.forEach((job) => {
        initialAppliedJobs[job.uuid] = job.candidate_job_apply_count === 1
      })
      setAppliedJobs(initialAppliedJobs)
    }
    window.scrollTo(0, 0)
  }, [jobs])

  // Prefill bookmark state when jobs are loaded
  useEffect(() => {
    if (jobs?.length > 0) {
      const initialBookmarks = {}
      jobs.forEach((job) => {
        initialBookmarks[job.uuid] = job.candidate_job_saved_count ? true : false
      })
      setBookmarkedJobs(initialBookmarks)
    }
    window.scrollTo(0, 0)
  }, [jobs])

  const handleBookmarkClick = async (jobId) => {
    const role = await new Promise((resolve) => {
      fetchUserRole((role) => resolve(role))
    })

    if (role !== "candidate") {
      // store intended path and redirect to login
      localStorage.setItem("redirectAfterLogin", window.location?.pathname)
      toast({
        title: "Please login first",
        description: "You need to login as a candidate to save or apply for a job.",
        status: "warning",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      })
      navigate("/login")
      return
    }

    const isCurrentlyBookmarked = bookmarkedJobs[jobId]
    const newStatus = isCurrentlyBookmarked ? "remove" : "save"

    setBookmarkedJobs((prev) => ({
      ...prev,
      [jobId]: !isCurrentlyBookmarked, // Optimistic UI update
    }))

    try {
      await dispatch(updateBookmarkAsync({ job_uuid: jobId, status: newStatus }))
      toast({
        title: `Job ${newStatus === "save" ? "saved" : "removed"} successfully!`,
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      })
    } catch (error) {
      console.error("Failed to update bookmark:", error)
      setBookmarkedJobs((prev) => ({
        ...prev,
        [jobId]: isCurrentlyBookmarked, // Revert on failure
      }))
      toast({
        title: "Failed to update bookmark.",
        description: "Something went wrong. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      })
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <JobCardSkeleton key={i} />
        ))}
      </div>
    )
  }

  if (!jobs?.length) {
    return (
      <NoDataFound
        title="No open positions at the moment"
        message="We couldn't find any open positions at the moment. Please check back later."
      />
    )
  }

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6 text-gray-800">Open Positions</h2>
      <div className="space-y-4">
        {jobs.map((job) => (
          <div
            key={job.uuid}
            className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 hover:shadow-md transition-shadow"
          >
            <div className="flex">
              {/* Logo */}
              <div className="flex-shrink-0 mr-4">
                <div className="h-14 w-14 rounded-lg overflow-hidden border border-gray-200 bg-white flex items-center justify-center p-1">
                  <Image
                    src={job.company_logo ? `${EMPLOYER_COMPANY_LOGO_SAVE_URL}${job.company_logo}` : companylogo}
                    name={job.name}
                    className="h-full w-full object-contain"
                    onError={(e) => (e.target.src = companylogo)}
                  />
                </div>
              </div>

              {/* Job Info */}
              <div className="flex-1">
                <Link to={`/jobs/${job.uuid}`} className="group">
                  <h3 className="text-xl text-left font-semibold text-gray-900 group-hover:text-blue-600 transition-colors flex items-center">
                    {job?.job_title?.title || job.job_name}
                    <Badge colorScheme="blue" className="ml-2" borderRadius="full">
                      {job.job_tag}
                    </Badge>
                  </h3>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-4 mt-3">
                    {/* Salary */}
                    <div className="flex items-center gap-2 text-gray-600 text-sm">
                      <CurrencyRupeeIcon className="w-4 h-4 text-gray-500" />
                      {job.salary_min_amount === null || job.salary_min_amount === 0 ? (
                        "Not Disclosed"
                      ) : (
                        <>
                          ₹{job.salary_min_amount || "N/A"} - ₹{job.salary_max_amount || "N/A"} /{" "}
                          {job.salary_per_type || "Not Specified"}
                        </>
                      )}
                    </div>

                    {/* Job Title Name */}
                    <div className="flex items-center gap-2 text-gray-600 text-sm">
                      <BriefcaseIcon className="w-4 h-4 text-gray-500" />
                      {job.job_name || "Job Name"}
                    </div>

                    {/* Created At */}
                    <div className="flex items-center gap-2 text-gray-600 text-sm">
                      <ClockIcon className="w-4 h-4 text-gray-500" />
                      <TimeDifference dateTimeString={job.created_at} />
                    </div>

                    {/* Work Location Type */}
                    <div className="flex items-center gap-2 text-gray-600 text-sm">
                      <BuildingOffice2Icon className="w-4 h-4 text-gray-500" />
                      {job.work_location_type || "Not Specified"}
                    </div>

                    {/* No of Hires */}
                    <div className="flex items-center gap-2 text-gray-600 text-sm">
                      <UsersIcon className="w-4 h-4 text-gray-500" />
                      {job.no_of_hire ? `${job.no_of_hire} openings` : "Not specified"}
                    </div>

                    {/* Location */}
                    {job.job_locations && job.job_locations.length > 0 && (
                      <div className="flex items-center gap-2 text-gray-600 text-sm">
                        <MapPinIcon className="w-4 h-4 text-gray-500" />
                        {job.job_locations.map((loc) => loc.job_location?.title).join(", ")}
                      </div>
                    )}
                  </div>
                </Link>
              </div>

              {/* Actions */}
              <div className="flex flex-col items-end justify-between ml-4">
                <button
                  onClick={() => handleBookmarkClick(job.uuid)}
                  className="bookmark p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  {bookmarkedJobs[job.uuid] ? (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="#9333ea" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M6 3C4.9 3 4 3.9 4 5V21L12 16.5L20 21V5C20 3.9 19.1 3 18 3H6Z"
                        stroke="#9333ea"
                        strokeWidth="2"
                      />
                    </svg>
                  ) : (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M6 3C4.9 3 4 3.9 4 5V21L12 16.5L20 21V5C20 3.9 19.1 3 18 3H6Z"
                        stroke="#64748b"
                        strokeWidth="2"
                      />
                    </svg>
                  )}
                </button>

                {appliedJobs[job.uuid] && (
                  <span className="text-green-600 text-sm font-semibold flex items-center gap-1 bg-green-50 px-3 py-1 rounded-full border border-green-100 mt-4">
                    ✅ Applied
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default JobsList

const CompanyMedia = ({ companyProfile }) => {
  const hasMedia = companyProfile && companyProfile.company_media?.length > 0

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6 text-gray-800">Company Gallery</h2>

      {hasMedia ? (
        <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {(() => {
              const videoItem = companyProfile.company_media.find((item) => item.type === "video")
              return videoItem ? (
                <div className="md:col-span-2 md:row-span-2 rounded-lg overflow-hidden h-[400px]">
                  <video controls className="w-full h-full object-cover">
                    <source src={`${EMPLOYER_COMPANY_MEDIA_URL}/${videoItem.file}`} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              ) : null
            })()}

            {companyProfile.company_media
              ?.filter((item) => item.type === "image")
              .slice(0, 5)
              .map((item, index) => (
                <div key={index} className="rounded-lg overflow-hidden h-[200px]">
                  <img
                    src={`${EMPLOYER_COMPANY_MEDIA_URL}/${item.file}`}
                    alt={`Gallery Image ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
          </div>
        </div>
      ) : (
        <GridItem colSpan={{ base: 1, md: 2 }}>
          <div>
            <NoDataFound
              title="No Media Found"
              message="We couldn't find any Media at the moment. Please check back later."
            />
          </div>
        </GridItem>
      )}
    </div>
  )
}

const ReviewsSidebar = ({ reviews, isLoading }) => {
  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} height="150px" rounded="xl" />
        ))}
      </div>
    )
  }

  if (!reviews?.length) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
        <NoDataFound
          title="No reviews yet"
          message="We couldn't find any Reviews at the moment. Please check back later."
        />
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
      <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
        <StarIcon className="w-5 h-5 text-yellow-500" />
        Recent Reviews
      </h3>

      <div className="space-y-6 max-h-[600px] overflow-y-auto pr-2">
        {reviews.map((review) => (
          <div key={review.uuid} className="border-b border-gray-100 pb-6 last:border-0 last:pb-0">
            <div className="flex items-center gap-2 mb-2">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <StarIcon
                    key={i}
                    className={`w-4 h-4 ${i < review.review_rating ? "text-yellow-500 fill-yellow-400" : "text-gray-300"
                      }`}
                  />
                ))}
              </div>
              <span className="text-sm text-gray-500">{review.review_rating}/5</span>
            </div>

            <h4 className="font-medium text-gray-900">{review.review_title}</h4>
            <p className="text-gray-600 mt-2 text-sm line-clamp-3">{review.review_description}</p>

            <div className="mt-3 text-xs text-gray-500 flex items-center gap-2">
              <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 font-medium text-xs">
                {review.candidate?.name?.charAt(0) || "U"}
              </div>
              <span>{review.candidate?.name}</span>
              <span>•</span>
              <span>{review.job_title?.title}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export function CompanySingle2() {
  const dispatch = useDispatch()
  const { uuid } = useParams()
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const toast = useToast()

  const cities = useSelector(selectJobCity)
  const jobTitles = useSelector(selectJobTitles)

  const companyProfile = useSelector(selectCompanyDetails)
  const companyJobs = useSelector(selectCompanyJobs)
  const reviews = useSelector(selectPublicCompanyReviews)

  // We only track company data here (token-free)
  const [fetchedData, setFetchedData] = useState({
    companyDetails: false,
    companyJobs: false,
    companyReviews: false,
  })

  const { isOpen, onOpen, onClose } = useDisclosure()
  const [reviewData, setReviewData] = useState({
    title: "",
    description: "",
    rating: 5,
    jobTitleUuid: "",
    jobLocationUuid: "",
  })
  const [errors, setErrors] = useState({})

  const navigate = useNavigate()

  // Helper: promise-based role getter
  const getUserRole = () =>
    new Promise((resolve) => {
      fetchUserRole((role) => resolve(role))
    })

  // Fetch protected lists (only after confirming candidate login)
  const ensureReviewDependencies = async () => {
    const promises = []
    if (!jobTitles || jobTitles.length === 0) {
      promises.push(dispatch(fetchPublicJobTitlesAsync()))
    }
    if (!cities || cities.length === 0) {
      promises.push(dispatch(fetchJobCityListAsync()))
    }
    if (promises.length) {
      await Promise.all(promises)
    }
  }

  const handleReviewClick = async () => {
    const role = await getUserRole()

    if (role === "candidate") {
      try {
        await ensureReviewDependencies()
        onOpen()
      } catch (err) {
        toast({
          title: "Error",
          description: "Failed to load form options. Please try again.",
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        })
      }
    } else {
      // Store the current page URL or path in localStorage before redirecting
      localStorage.setItem("redirectAfterLogin", window.location?.pathname)
      toast({
        title: "Please login first",
        description: "You need to login as a candidate to write a review.",
        status: "warning",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      })
      navigate("/login")
    }
  }

  const handleReviewSubmit = async () => {
    const { title, description, rating, jobTitleUuid, jobLocationUuid } = reviewData
    const tempErrors = {}

    if (!jobTitleUuid) tempErrors.jobTitleUuid = "Designation is required."
    if (!jobLocationUuid) tempErrors.jobLocationUuid = "Location is required."
    if (!title || !title.trim()) tempErrors.title = "Review title is required."
    if (!description || !description.trim()) tempErrors.description = "Review description is required."

    if (Object.keys(tempErrors).length > 0) {
      setErrors(tempErrors)
      return
    }

    try {
      await dispatch(
        addCompanyReviewAsync({
          company_uuid: uuid,
          job_title_uuid: jobTitleUuid,
          job_location_uuid: jobLocationUuid,
          review_title: title.trim(),
          review_rating: rating,
          review_description: description.trim(),
        }),
      )

      toast({
        title: "Review Submitted",
        description: "Thank you for your feedback!",
        status: "success",
        duration: 3000,
        position: "top-right",
      })

      onClose()
      setReviewData({
        title: "",
        description: "",
        rating: 5,
        jobTitleUuid: "",
        jobLocationUuid: "",
      })
      setErrors({})
      dispatch(fetchPublicCompanyReviewsAsync({ uuid }))
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to submit review. Please try again.",
        status: "error",
        duration: 3000,
        position: "top-right",
      })
    }
  }

  // Only fetch public company data here; don't fetch jobTitles/cities (require token)
  useEffect(() => {
    if (!uuid) return

    const fetchInitialData = async () => {
      setIsLoading(true)
      setError(null)

      try {
        const promises = []

        if (!fetchedData.companyDetails) {
          promises.push(dispatch(fetchCompanyDetailsAsync({ uuid })))
          setFetchedData((prev) => ({ ...prev, companyDetails: true }))
        }

        if (!fetchedData.companyJobs) {
          promises.push(dispatch(fetchJobListByCompanyAsync({ uuid })))
          setFetchedData((prev) => ({ ...prev, companyJobs: true }))
        }

        if (!fetchedData.companyReviews) {
          promises.push(dispatch(fetchPublicCompanyReviewsAsync({ uuid })))
          setFetchedData((prev) => ({ ...prev, companyReviews: true }))
        }

        if (promises.length > 0) {
          await Promise.all(promises)
        }
      } catch (error) {
        setError("Failed to load company data. Please try again later.")
        console.error("Error fetching data:", error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchInitialData()
    window.scrollTo(0, 0)
  }, [uuid, dispatch, fetchedData])

  const isSubmitDisabled =
    !reviewData.jobTitleUuid ||
    !reviewData.jobLocationUuid ||
    !reviewData.title.trim() ||
    !reviewData.description.trim()

  return (
    <Box minHeight="100vh" className="bg-gray-50">
      <CompanyOverview
        companyProfile={companyProfile}
        companyJobs={companyJobs}
        reviews={reviews}
        handleOpenReviewModal={handleReviewClick}
        isLoading={isLoading}
        error={error}
      />
      <Footer />

      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent borderRadius="lg">
          <ModalHeader className="bg-blue-600 text-white rounded-t-lg">Write a Review</ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody className="p-4">
            <VStack spacing={5}>
              <FormControl isInvalid={!!errors.jobTitleUuid}>
                <Select
                  placeholder="Select Designation"
                  value={reviewData.jobTitleUuid}
                  onChange={(e) => {
                    setReviewData({
                      ...reviewData,
                      jobTitleUuid: e.target.value,
                    })
                    setErrors({ ...errors, jobTitleUuid: "" })
                  }}
                  borderRadius="md"
                  size="md"
                >
                  {jobTitles &&
                    jobTitles.map((job) => (
                      <option key={job.uuid} value={job.uuid}>
                        {job.title}
                      </option>
                    ))}
                </Select>
                {errors.jobTitleUuid && (
                  <Text color="red.500" fontSize="sm" mt={1}>
                    {errors.jobTitleUuid}
                  </Text>
                )}
              </FormControl>

              <FormControl isInvalid={!!errors.jobLocationUuid}>
                <Select
                  placeholder="Select Location"
                  value={reviewData.jobLocationUuid}
                  onChange={(e) => {
                    setReviewData({
                      ...reviewData,
                      jobLocationUuid: e.target.value,
                    })
                    setErrors({ ...errors, jobLocationUuid: "" })
                  }}
                  borderRadius="md"
                  size="md"
                >
                  {cities &&
                    cities.map((city) => (
                      <option key={city.uuid} value={city.uuid}>
                        {city.title}
                      </option>
                    ))}
                </Select>
                {errors.jobLocationUuid && (
                  <Text color="red.500" fontSize="sm" mt={1}>
                    {errors.jobLocationUuid}
                  </Text>
                )}
              </FormControl>

              <FormControl isInvalid={!!errors.title}>
                <Input
                  placeholder="Review Title"
                  value={reviewData.title}
                  onChange={(e) => {
                    setReviewData({ ...reviewData, title: e.target.value })
                    setErrors({ ...errors, title: "" })
                  }}
                  borderRadius="md"
                  size="md"
                />
                {errors.title && (
                  <Text color="red.500" fontSize="sm" mt={1}>
                    {errors.title}
                  </Text>
                )}
              </FormControl>

              <FormControl isInvalid={!!errors.description}>
                <Textarea
                  placeholder="Write your review..."
                  value={reviewData.description}
                  onChange={(e) => {
                    setReviewData({
                      ...reviewData,
                      description: e.target.value,
                    })
                    setErrors({ ...errors, description: "" })
                  }}
                  rows={5}
                  borderRadius="md"
                />
                {errors.description && (
                  <Text color="red.500" fontSize="sm" mt={1}>
                    {errors.description}
                  </Text>
                )}
              </FormControl>

              <div className="w-full">
                <Text mb={2} fontWeight="medium">
                  Rating
                </Text>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      onClick={() =>
                        setReviewData({
                          ...reviewData,
                          rating: star,
                        })
                      }
                      className={`p-2 rounded-full transition-all ${star <= reviewData.rating ? "text-yellow-400 scale-110" : "text-gray-300 hover:text-gray-400"
                        }`}
                    >
                      <StarIcon className="w-8 h-8 fill-current" />
                    </button>
                  ))}
                </div>
              </div>
            </VStack>
          </ModalBody>
          <ModalFooter className="space-x-4">
            <Button variant="outline" onClick={onClose} borderRadius="md">
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleReviewSubmit} borderRadius="md" isDisabled={isSubmitDisabled}>
              Submit Review
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  )
}